import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';



import ProjectOverview from './components/ProjectOverview';
import TaskManager from './components/TaskManager';
import FileManager from './components/FileManager';
import TeamChat from './components/TeamChat';
import MeetingScheduler from './components/MeetingScheduler';
import ProgressTracker from './components/ProgressTracker';
import QuickActions from './components/QuickActions';
import NotificationsPreview from './components/NotificationsPreview';

import AIChatbot from '../../components/ui/AIChatbot';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const TeamWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Create custom menu for Header component
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'tasks', label: 'Tasks', icon: 'CheckSquare' },
    { id: 'files', label: 'Files', icon: 'FolderOpen' },
    { id: 'chat', label: 'Chat', icon: 'MessageSquare' },
    { id: 'meetings', label: 'Meetings', icon: 'Calendar' },
    { id: 'progress', label: 'Progress', icon: 'TrendingUp' }
  ];

  const customHeaderMenu = tabs?.map(tab => ({
    id: tab?.id,
    label: tab?.label,
    icon: tab?.icon,
    onClick: () => setActiveTab(tab?.id),
    active: activeTab === tab?.id
  }));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle active tab from navigation state (for Quick Actions)
  useEffect(() => {
    if (location?.state?.activeTab) {
      setActiveTab(location?.state?.activeTab);
    }
  }, [location?.state]);

  // 🔹 Fetch progress data function - moved to component scope
  const fetchProgressData = async () => {
    if (!teamInfo?.id) return;

    try {
      // 1️⃣ Get all stages
      const { data: stages, error: stagesError } = await supabase?.from('project_stages')?.select('*')?.order('stage_number', { ascending: true });

      if (stagesError) throw stagesError;

      // 2️⃣ Get team's progress
      const { data: progress, error: progressError } = await supabase?.from('team_stage_progress')?.select(`
          stage_id,
          status,
          uploads_count,
          last_uploaded_at,
          feedback
        `)?.eq('team_id', teamInfo?.id);

      if (progressError) throw progressError;

      // 3️⃣ Merge data
      const mergedMilestones = stages?.map(stage => {
        const teamStage = progress?.find(p => p?.stage_id === stage?.id);
        return {
          id: stage?.id,
          name: stage?.name,
          description: stage?.description,
          dueDate: stage?.stage_deadline,
          status: teamStage?.status || 'pending',
          uploadsCount: teamStage?.uploads_count || 0,
          completedAt: teamStage?.status === 'completed' ? teamStage?.last_uploaded_at : null,
          feedback: teamStage?.feedback || null,
          requiredUploads: stage?.required_uploads
        };
      });

      setMilestones(mergedMilestones);
    } catch (err) {
      console.error('❌ Error loading milestones:', err?.message);
    }
  };

  // 🔹 Fetch team and user info
  useEffect(() => {
    async function fetchTeamAndMembers() {
      try {
        setLoading(true);
        if (!user?.email) return;

        // Step 1: Auth user
        const {
          data: { user: authUser },
          error: userError,
        } = await supabase?.auth?.getUser();

        if (userError || !authUser) throw userError;

        // Step 2: User profile
        const { data: userProfile, error: profileError } = await supabase?.from('user_profiles')?.select(`
            *,
            team:teams!user_profiles_team_id_fkey (
              id,
              team_name,
              team_number
            )
          `)?.eq('id', authUser?.id)?.single();

        if (profileError) throw profileError;

        // Step 3: Resolve team number
        let teamNumber = userProfile?.team?.team_number;
        if (!teamNumber) {
          const { data: roleAuth, error: roleError } = await supabase?.from('role_email_authorizations')?.select('team_number')?.eq('email', authUser?.email)?.maybeSingle();
          if (roleError) throw roleError;
          teamNumber = roleAuth?.team_number;
        }

        // Step 4: Fetch team info
        const { data: teamRow, error: teamError } = await supabase?.from('teams')?.select('*')?.eq('team_number', teamNumber)?.single();
        if (teamError) throw teamError;

        // Step 5: Team members
        const { data: members, error: membersError } = await supabase?.from('user_profiles')?.select('id, full_name, role, team_role, email, team_id, teams(team_number)')?.eq('team_id', teamRow?.id);
        if (membersError) throw membersError;

        // ✅ Update state
        setCurrentUser({
          id: userProfile?.id,
          name: userProfile?.full_name,
          role: userProfile?.team_role || userProfile?.role,
          email: userProfile?.email,
        });
        setTeamInfo(teamRow);
        setTeamMembers(members);
      } catch (err) {
        console.error('❌ Error fetching team:', err?.message);
      } finally {
        setLoading(false);
      }
    }

    if (user) fetchTeamAndMembers();
  }, [user]);

  // 🔹 Load progress data when teamInfo changes
  useEffect(() => {
    if (teamInfo?.id) {
      fetchProgressData();
    }
  }, [teamInfo]);

  // 🔹 Subscribe to uploads for real-time updates
  useEffect(() => {
    if (!teamInfo?.id) return;

    const uploadsChannel = supabase?.channel('team_uploads')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_stage_uploads',
          filter: `team_id=eq.${teamInfo?.id}`,
        },
        (payload) => {
          console.log('📥 New upload detected:', payload);
          // re-fetch milestones when uploads change
          fetchProgressData();
        }
      )?.subscribe();

    return () => supabase?.removeChannel(uploadsChannel);
  }, [teamInfo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-muted-foreground">
        Loading workspace...
      </div>
    );
  }

  // 🔹 Tab rendering
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-card rounded-lg border border-border">
                <ProjectOverview
                  project={teamInfo}
                  team={{ members: teamMembers }}
                  currentPhase={teamInfo?.current_phase || 'planning'}
                />
              </div>
              <ProgressTracker
                project={teamInfo}
                milestones={milestones}
                deliverables={deliverables}
                teamId={teamInfo?.id}
              />
              <NotificationsPreview />
            </div>
             <div>
              <QuickActions
                project={teamInfo}
                team={{ 
                  name: teamInfo?.team_name, 
                  members: teamMembers,
                  mentor: teamInfo?.mentor,
                  city_official_contact: teamInfo?.city_official_contact
                }}
              />
            </div> 
          </div>
        );
      case 'progress':
        return (
          <ProgressTracker
            project={teamInfo}
            milestones={milestones}
            deliverables={deliverables}
            teamId={teamInfo?.id}
          />
        );
      case 'files':
        return (
          <FileManager
            files={deliverables}
            teamId={teamInfo?.id}
            userId={currentUser?.id}
            onUploadSuccess={fetchProgressData}
          />
        );
      case 'chat':
        return <TeamChat messages={[]} teamMembers={teamMembers} currentUser={currentUser} onSendMessage={(message) => console.log('Message sent:', message)} />;
      case 'tasks':
        return <TaskManager tasks={[]} teamMembers={teamMembers} teamId={teamInfo?.id} onTaskUpdate={(task) => console.log('Task updated:', task)} onTaskCreate={(task) => console.log('Task created:', task)} />;
      case 'meetings':
        return (
          <MeetingScheduler 
            teamId={teamInfo?.id}
            teamMembers={teamMembers?.map(member => ({
              id: member?.id,
              name: member?.full_name,
              email: member?.email,
              role: member?.team_role || member?.role
            }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={currentUser?.role || 'student'}
        userName={currentUser?.name}
        notifications={3}
        customMenu={customHeaderMenu}
      />
      <main className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {teamInfo?.team_name || 'Team Workspace'}
            </h1>
            <p className="text-muted-foreground">
              Collaborate with your team on {teamInfo?.project_title || 'your project'}
            </p>
          </div>
        </div>
        <div className="space-y-6">{renderTabContent()}</div>
      </main>
      
      {/* AI Chatbot */}
      <AIChatbot userRole="student" userId={user?.id} />
    </div>
  );
};

export default TeamWorkspace;